import RetailModel from 'app/src/data/models/RetailModel';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import UUID from 'app/src/data/base/UUID';

const CARD_DETAILS_ATTRIBUTE = 'CardRecord';
const CARD_TRANSACTION_DETAILS_ATTRIBUTE = 'CardTransactionDetails';
const DEBIT_CARD_TYPE = 'DEBIT';
const TRANSACTION_PARAMETERS = 'parameters';

const _OperationType = {
  SALE: 'sale',
  VOID: 'void',
  REFUND: 'refund',
  /** This is used when the payment's type cannot be derived from its transactions or its workflow */
  UNKNOWN: 'unknown',
};

const _SupportedCardType = {
  VISA: 'visa',
  MASTERCARD: 'master-card',
  DISCOVER: 'discover',
  AMERICAN_EXPRESS: 'american-express',
  DEBIT: 'debit',
  INTERAC: 'interac',
  BANCONTACT: 'bancontact',
  IDEAL: 'ideal',
};

export default class PaymentTransaction extends RetailModel {
  static get OperationType() {
    return _OperationType;
  }

  static get SupportedCardType() {
    return _SupportedCardType;
  }

  // Server uses the optionID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'paymentTransactionID';
  }

  get defaults() {
    return {
      gateway: '',
      paymentID: '',
      terminalID: '',
    };
  }

  get retailResponseName() {
    return 'PaymentTransaction';
  }

  /**
   * Return the url for the Sale endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/PaymentTransaction');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Sets the request (URL) used to initiate the transaction with the terminal.
   *
   * @param gatewayRequest {String}    The request URL used with the terminal to initiate the transaction
   */
  set gatewayRequest(gatewayRequest) {
    let additionalValues = this.get(CARD_TRANSACTION_DETAILS_ATTRIBUTE) || {};
    let encodedRequest = gatewayRequest;

    if (_.isObject(gatewayRequest)) {
      encodedRequest = btoa(JSON.stringify(encodedRequest));
    } else {
      encodedRequest = btoa(encodedRequest);
    }

    additionalValues.gatewayRequest = encodedRequest;
    this.set(CARD_TRANSACTION_DETAILS_ATTRIBUTE, additionalValues);
  }

  /**
   * Gets the request (URL) used to initiate the transaction with the terminal.
   *
   * @returns {String}    The request URL used with the terminal to initiate the transaction
   */
  get gatewayRequest() {
    let gatewayRequest = null;
    let transactionDetails = this.get(CARD_TRANSACTION_DETAILS_ATTRIBUTE);

    if (transactionDetails) {
      gatewayRequest = transactionDetails.gatewayRequest;
    }

    return gatewayRequest;
  }

  /**
   * Sets the raw response received from the terminal for a request.
   *
   * @param gatewayResponse {String}    The raw response from the terminal
   */
  set gatewayResponse(gatewayResponse) {
    let additionalValues = this.get(CARD_TRANSACTION_DETAILS_ATTRIBUTE) || {};

    let encodedResponse = gatewayResponse;

    if (_.isObject(gatewayResponse)) {
      encodedResponse = btoa(JSON.stringify(encodedResponse));
    } else {
      encodedResponse = btoa(encodedResponse);
    }

    additionalValues.gatewayResponse = encodedResponse;
    this.set(CARD_TRANSACTION_DETAILS_ATTRIBUTE, additionalValues);
  }

  /**
   * Gets the raw response received from the terminal for a request.
   *
   * @returns {String}    The request URL used with the terminal to initiate the transaction
   */
  get gatewayResponse() {
    let gatewayResponse = null;
    let transactionDetails = this.get(CARD_TRANSACTION_DETAILS_ATTRIBUTE);

    if (transactionDetails) {
      gatewayResponse = transactionDetails.gatewayResponse;
    }

    return gatewayResponse;
  }

  /**
   * Get the card holder name from the transaction details.
   *
   * @returns {string}    The card holder name when present or an empty string
   */
  get cardHolderName() {
    let cardHolderName = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      cardHolderName = this.get(CARD_DETAILS_ATTRIBUTE).cardHolderName || cardHolderName;
    }

    return cardHolderName;
  }

  /**
   * Get the card brand from the transaction details.
   *
   * @returns {string}    The card brand present or an empty string
   */
  get cardBrand() {
    let cardBrand = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      cardBrand = this.get(CARD_DETAILS_ATTRIBUTE).cardBrand || cardBrand;
    }

    return cardBrand;
  }

  /**
   * Get the card number from the transaction details.
   *
   * @returns {string}    The card number present or an empty string
   */
  get cardNumber() {
    let cardNumber = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      cardNumber = this.get(CARD_DETAILS_ATTRIBUTE).cardNumber || cardNumber;
    }

    return cardNumber;
  }

  /**
   * Get the card type from the transaction details.
   *
   * @returns {string}    The card type when present or an empty string
   */
  get cardType() {
    let cardType = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      cardType = this.get(CARD_DETAILS_ATTRIBUTE).cardType || cardType;
    }

    return cardType;
  }

  /**
   * Get the entry method from the transaction details.
   *
   * @returns {string}    The entry method when present or an empty string
   */
  get entryMethod() {
    let entryMethod = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      entryMethod = this.get(CARD_DETAILS_ATTRIBUTE).entryMethod || entryMethod;
    }

    return entryMethod;
  }

  /**
   * Get the expiry date from the transaction details.
   *
   * @returns {string}    The expiry date when present or an empty string
   */
  get expiryDate() {
    let expiryDate = '';

    if (this.has(CARD_DETAILS_ATTRIBUTE)) {
      expiryDate = this.get(CARD_DETAILS_ATTRIBUTE).expiryDate || expiryDate;
    }

    return expiryDate;
  }

  /**
   * Get the parameters from the the transaction.
   */
  get parameters() {
    return this.has(TRANSACTION_PARAMETERS) ? this.get(TRANSACTION_PARAMETERS) : null;
  }

  /**
   * Determines if the payment transaction is for a debit card
   *
   * @returns {Number|boolean}    True when the transaction was a debit transaction
   */
  isDebit() {
    return this.cardType.length > 0 && this.cardType.toUpperCase() === DEBIT_CARD_TYPE;
  }

  /**
   * Determines if the payment transaction is for an Interac Card
   *
   * @returns {boolean} True when the transaction was an Interac transaction
   */
  isInterac() {
    return this.cardType.length > 0 && this.cardType === _SupportedCardType.INTERAC;
  }
}
